import { useEffect } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useRefreshSession } from '@/hooks/useRefreshSession';
import { peekPurchaseIntent, readPurchaseIntent } from '@/lib/auth/intent-storage';

interface StartupCreatedState {
  entityId?: string;
  companyName?: string;
}

export function StartupCreatedPage() {
  const location = useLocation();
  const state = location.state as StartupCreatedState | null;
  const entityId = state?.entityId;
  const navigate = useNavigate();
  const { mutate: refreshSession, isPending } = useRefreshSession();
  // The Issue Report journey is in progress (FR-206): once the refreshed
  // session shows the Startup Admin role, hand back to the journey route.
  const inJourney = peekPurchaseIntent() !== null;

  // Becoming a Startup Admin is a server-side role change; re-issue the session
  // so nav/landing and the startup routes unlock without a full reload (MYN-46
  // D1). Only when this is a genuine post-create arrival (guarded so the
  // redirect path below does not trigger a spurious refresh).
  useEffect(() => {
    if (!entityId) return;
    refreshSession(undefined, {
      // Only after the refresh resolves, so checkout never opens against a
      // session that does not yet hold the role (FR-205).
      onSuccess: () => {
        if (readPurchaseIntent()) navigate(ROUTES.ISSUE_REPORT_START, { replace: true });
      },
    });
  }, [entityId, refreshSession, navigate]);

  // Direct navigation (no create result in router state) has nothing to confirm.
  if (!entityId) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // Waiting for the refresh, never a guard's Access Denied (Card 27 HANDLE 3).
  if (inJourney && isPending) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-page-bg px-4 py-10">
      <div className="w-full max-w-xl rounded-card bg-white p-10 text-center shadow-card">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green"
          aria-hidden="true"
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" className="text-white">
            <path
              d="M20 6L9 17l-5-5"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="font-sans text-h1 text-navy">{UI_STRINGS.STARTUP_CREATED.HEADING}</h1>
        <p className="mx-auto mt-3 mb-8 max-w-md text-sm text-subtext">
          {UI_STRINGS.STARTUP_CREATED.SUBTITLE}
        </p>

        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button asChild variant="primary">
            <Link to={ROUTES.DASHBOARD}>{UI_STRINGS.STARTUP_CREATED.GO_TO_DASHBOARD}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={ROUTES.STARTUP_PROFILE}>{UI_STRINGS.STARTUP_CREATED.VIEW_PROFILE}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
